// Each grid section loads this file, so a page with two of them (or a grid
// under a product page) loads it twice. The second copy does nothing instead
// of throwing "already declared". The body isn't re-indented, to keep history.
(() => {
if (window.__voraxCollectionsLoaded) return;
window.__voraxCollectionsLoaded = true;

// Variant images are fetched cold from the CDN, so the browser keeps painting
// the previous image until the new one is decoded. Warming them up ahead of the
// click is what makes the swap feel instant.

// Only dim the image when the swap is actually waiting on the network, so a
// preloaded image never flashes.
const IMAGE_SWAP_FEEDBACK_DELAY = 120;

// Speculative warming must never saturate the connection: a click that lands
// mid warm-up has to win the bandwidth, not queue behind the whole grid.
const MAX_CONCURRENT_PRELOADS = 2;

const requestedImages = new Map();
const warmedImages = new Set();
const preloadQueue = [];

let activePreloads = 0;

function loadImage(src, priority) {
  const image = new Image();

  const pending = new Promise((resolve) => {
    image.addEventListener("load", () => resolve(image), { once: true });
    image.addEventListener("error", () => resolve(null), { once: true });
  });

  image.decoding = "async";

  if ("fetchPriority" in image) image.fetchPriority = priority;

  image.src = src;

  return pending;
}

// On demand: somebody is looking at a blank swap right now, so this one never
// queues and asks for high priority. Identical in-flight image requests are
// coalesced by the browser, so a warm-up already running is reused, not doubled.
function fetchImage(src) {
  if (!src) return Promise.resolve(null);

  if (requestedImages.has(src)) return requestedImages.get(src);

  const pending = loadImage(src, "high");

  requestedImages.set(src, pending);

  return pending;
}

// Speculative: capped, low priority, and safe to drop on the floor.
function preloadImage(src) {
  if (!src) return;

  if (warmedImages.has(src) || requestedImages.has(src)) return;

  warmedImages.add(src);

  preloadQueue.push(src);

  drainPreloadQueue();
}

function drainPreloadQueue() {
  while (activePreloads < MAX_CONCURRENT_PRELOADS && preloadQueue.length) {
    const src = preloadQueue.shift();

    activePreloads += 1;

    loadImage(src, "low").then(() => {
      activePreloads -= 1;

      drainPreloadQueue();
    });
  }
}

function whenIdle(callback) {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(callback, { timeout: 2000 });
    return;
  }

  setTimeout(callback, 1000);
}

// Warming a card nobody has scrolled to yet only steals bandwidth from the one
// on screen.
function whenNearViewport(element, callback) {
  if (typeof IntersectionObserver !== "function") {
    callback();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer.disconnect();

      callback();
    },
    { rootMargin: "200px" },
  );

  observer.observe(element);
}

class ProductCard {
  constructor(element) {
    this.element = element;

    // A general card holds its product data. Thermo cards share one copy per
    // product in the grid; each card parses its own, so its state stays apart.
    const data =
      element.querySelector(".product-data") ??
      (element.closest("#CollectionProducts") ?? document).querySelector(
        `.product-data[data-product-id="${element.dataset.productId}"]`,
      );

    this.rawProduct = JSON.parse(data.textContent);

    this.product = this.normalizeProduct(this.rawProduct);

    this.defaultVariantId = Number(element.dataset.defaultVariantId) || null;

    // Size on apparel, Capacity on bottles. Read by position, because option
    // names are translated on /es/.
    this.quickPosition = Number(element.dataset.quickOptionPosition) || null;

    this.state = this.getInitialState();

    this.elements = this.cacheElements();

    this.bindEvents();

    this.render();

    this.schedulePreload();

    this.element.productCard = this;
  }

  normalizeProduct(product) {
    const optionIndex = {};
    const optionValueIndex = {};
    const variantIndex = {};

    product.options.forEach((option) => {
      optionIndex[option.name] = option;

      optionValueIndex[option.name] = {};

      option.values.forEach((value) => {
        optionValueIndex[option.name][value.value] = value;
      });
    });

    product.variants.forEach((variant) => {
      const key = this.buildVariantKey(
        product.options,
        variant.selectedOptions,
      );

      variant.key = key;

      variantIndex[key] = variant;
    });

    return {
      ...product,
      optionIndex,
      optionValueIndex,
      variantIndex,
    };
  }

  buildVariantKey(options, selectedOptions) {
    return options
      .map((option) => selectedOptions[option.name] ?? "")
      .join("|");
  }

  getInitialState() {
    const { variants } = this.product;

    // By id, so it holds in every language.
    const initialVariant =
      variants.find((variant) => variant.id === this.defaultVariantId) ??
      variants.find((variant) => variant.available) ??
      variants[0];

    return {
      selectedOptions: {
        ...initialVariant.selectedOptions,
      },

      // Whether the shopper picked the quick option (size/capacity) on this
      // card. The default one is never added on their behalf.
      quickChosen: false,

      // Add was pressed before a size was picked, so the panel is asking.
      awaitingChoice: false,
    };
  }

  getCurrentVariant() {
    return this.findVariant(this.state.selectedOptions);
  }

  findVariant(selectedOptions) {
    const key = this.buildVariantKey(this.product.options, selectedOptions);

    return this.product.variantIndex[key] ?? null;
  }

  // Colour is always option 1.
  getColorOption() {
    return this.product.options.find((option) => option.position === 1) ?? null;
  }

  getQuickOption() {
    return (
      this.product.options.find(
        (option) => option.position === this.quickPosition,
      ) ?? null
    );
  }

  // The quick option's values that exist with the colour (and anything else)
  // currently selected: Smoky dusk only comes in 24oz.
  getQuickValues() {
    const option = this.getQuickOption();

    if (!option) return [];

    return option.values.filter((value) =>
      this.findVariant({
        ...this.state.selectedOptions,
        [option.name]: value.name,
      }),
    );
  }

  // Hats, socks and single-capacity colours have nothing to choose, so they
  // add straight away.
  needsChoice() {
    return this.getQuickValues().length > 1;
  }

  // A colour without the selected capacity would leave the card on a variant
  // that doesn't exist. Move to the closest one that does, preferring stock.
  closestVariant(option, value) {
    const current = this.state.selectedOptions;

    const score = (variant) =>
      (variant.available ? this.product.options.length : 0) +
      this.product.options.filter(
        ({ name }) => variant.selectedOptions[name] === current[name],
      ).length;

    return (
      this.product.variants
        .filter((variant) => variant.selectedOptions[option] === value)
        .sort((a, b) => score(b) - score(a))[0] ?? null
    );
  }

  getWishlistData() {
    const variant = this.getCurrentVariant();

    if (!variant) return null;

    return {
      productId: this.product.id,
      variantId: variant.id,
      handle: this.product.handle,
    };
  }

  getModalData() {
    const variant = this.getCurrentVariant();

    if (!variant) return null;

    return {
      productId: this.product.id,
      variantId: variant.id,

      title: this.product.title,
      handle: this.product.handle,

      url: variant.url,

      image: variant.media.items[0] ?? null,

      price: variant.price,
      compareAtPrice: variant.compareAtPrice,

      selectedOptions: {
        ...variant.selectedOptions,
      },

      available: variant.available,

      inventory: {
        ...variant.inventory,
      },
    };
  }
  // Touch screens use the size sheet too: the hover panel needs a mouse.
  isMobile() {
    return (
      window.innerWidth < 768 || window.matchMedia("(hover: none)").matches
    );
  }
  cacheElements() {
    return {
      media: this.element.querySelector(".collection-product-media"),

      link: this.element.querySelector(".collection-product-link"),

      containerSizes: this.element.querySelector(
        ".collection-product-container-sizes",
      ),

      quickLabel: this.element.querySelector(".collection-product-quick-label"),

      title: this.element.querySelector(".collection-product-title"),

      description: this.element.querySelector(
        ".collection-product-description",
      ),

      favoriteButton: this.element.querySelector(".favorite-btn"),

      image: this.element.querySelector(".collection-product-image"),

      price: this.element.querySelector(".price-variant"),

      optionButtons: [...this.element.querySelectorAll("[data-option]")],

      addToCart: this.element.querySelector(".addToCart"),
    };
  }

  bindEvents() {
    this.elements.optionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.onOptionClick(button);
      });

      // Warm the option's images as soon as the intent shows up, so the click
      // itself has nothing left to download.
      ["mouseenter", "touchstart", "focus"].forEach((type) => {
        button.addEventListener(type, () => this.preloadOption(button), {
          passive: true,
        });
      });
    });

    this.elements.media.addEventListener("mouseenter", () => {
      this.onMouseEnter();
    });

    this.elements.media.addEventListener("mouseleave", () => {
      this.onMouseLeave();
    });

    this.elements.addToCart.addEventListener("click", () => {
      this.onAddToCartClick();
    });

    this.elements.favoriteButton.addEventListener(
      "click",
      this.onFavoriteClick.bind(this),
    );
  }

  onMouseEnter() {
    if (this.isMobile()) return;
    const variant = this.getCurrentVariant();

    if (!variant) return;

    this.elements.containerSizes.classList.remove("opacity-0", "invisible");

    if (variant.media.items.length < 2) return;

    this.state.isHovering = true;

    this.renderImage(variant);
  }

  onMouseLeave() {
    if (this.isMobile()) return;
    const variant = this.getCurrentVariant();

    this.elements.containerSizes.classList.add("opacity-0", "invisible");
    this.state.isHovering = false;

    if (this.state.awaitingChoice) {
      this.state.awaitingChoice = false;

      this.renderQuickPanel();
    }

    if (!variant) return;

    this.renderImage(variant);
  }

  async onAddToCartClick() {
    if (this.elements.addToCart.getAttribute("aria-disabled") === "true") {
      return;
    }

    if (!this.needsChoice()) {
      await this.addToCart(this.getCurrentVariant());
      return;
    }

    if (this.isMobile()) {
      mobileSizeSheet.open(this);
      return;
    }

    // Desktop adds only a size the shopper picked, never the default one.
    const variant = this.getCurrentVariant();

    if (this.state.quickChosen && variant?.available) {
      await this.addToCart(variant);
      return;
    }

    // Ask instead. The next size picked in the panel completes the add.
    this.state.awaitingChoice = true;

    this.renderQuickPanel();

    // Keyboard users can't hover, so show the panel and move them into it.
    this.elements.containerSizes.classList.remove("opacity-0", "invisible");

    const quickOption = this.getQuickOption();

    this.elements.optionButtons
      .find(
        (button) =>
          button.dataset.option === quickOption.name && !button.disabled,
      )
      ?.focus();
  }

  // Dawn's pattern: aria-disabled + .loading while the request runs, cleared in
  // finally, so rapid taps add exactly one unit.
  async addToCart(variant) {
    const button = this.elements.addToCart;

    if (!variant || button.getAttribute("aria-disabled") === "true") return;

    button.setAttribute("aria-disabled", "true");
    button.classList.add("loading");

    try {
      await window.addVariantToCart(variant.id);
    } catch (error) {
      // Cart errors are already shown in the cart pop-up. A 422 means this
      // variant can't be added any more (sold out, or all of it is already in
      // the cart), so stop offering it on this card.
      if (error.status === 422) {
        variant.available = false;

        this.state.quickChosen = false;

        this.render();
      }

      if (error.status === undefined) console.error(error);
    } finally {
      button.classList.remove("loading");
      button.removeAttribute("aria-disabled");
    }
  }

  openMobileSizePicker() {
    mobileSizeSheet.open(this);
  }

  onFavoriteClick() {
    const product = this.getWishlistData();

    if (!product) return;

    App.wishlist.toggle(product);

    this.renderWishlist();
  }

  onOptionClick(button) {
    const option = button.dataset.option;
    const value = button.dataset.value;

    if (button.disabled) return;

    const isQuick = option === this.getQuickOption()?.name;
    const completesAdd = isQuick && this.state.awaitingChoice;

    if (isQuick) {
      this.state.quickChosen = true;
      this.state.awaitingChoice = false;
    }

    this.updateSelectedOption(option, value);

    if (completesAdd) this.addToCart(this.getCurrentVariant());
  }

  isOptionAvailable(option, value) {
    const selectedOptions = {
      ...this.state.selectedOptions,
      [option]: value,
    };

    const key = this.buildVariantKey(this.product.options, selectedOptions);

    const variant = this.product.variantIndex[key];

    if (!variant || !variant.available) {
      return false;
    }

    const inventory = variant.inventory;

    // El producto no maneja inventario
    if (!inventory.managed) {
      return true;
    }

    // Permite vender sin stock
    if (inventory.policy === "continue") {
      return true;
    }

    // Debe tener inventario disponible
    return inventory.quantity > 0;
  }

  schedulePreload() {
    whenNearViewport(this.element, () => {
      whenIdle(() => this.preloadVariantImages());
    });
  }

  preloadVariantImages() {
    const seen = new Set();
    const colorOption = this.getColorOption();

    this.product.variants.forEach((variant) => {
      const color = colorOption && variant.selectedOptions[colorOption.name];

      if (color) {
        if (seen.has(color)) return;

        seen.add(color);
      }

      const first = variant.media.items[0];

      if (first) preloadImage(first.src);
    });

    // The hover image of the variant on screen is the other likely next paint.
    const current = this.getCurrentVariant();
    const hoverImage = current?.media.items[1];

    if (hoverImage) preloadImage(hoverImage.src);
  }

  preloadOption(button) {
    const selectedOptions = {
      ...this.state.selectedOptions,
      [button.dataset.option]: button.dataset.value,
    };

    const key = this.buildVariantKey(this.product.options, selectedOptions);

    const variant = this.product.variantIndex[key];

    if (!variant) return;

    const [first, hover] = variant.media.items;

    // The click is one gesture away, so this is the image to win the bandwidth.
    if (first) fetchImage(first.src);

    if (hover) preloadImage(hover.src);
  }

  updateSelectedOption(option, value) {
    const selectedOptions = { ...this.state.selectedOptions, [option]: value };

    if (this.findVariant(selectedOptions)) {
      this.state.selectedOptions = selectedOptions;
    } else {
      const closest = this.closestVariant(option, value);
      const quickName = this.getQuickOption()?.name;

      if (closest) {
        // The size moved without the shopper picking it.
        if (
          closest.selectedOptions[quickName] !==
          this.state.selectedOptions[quickName]
        ) {
          this.state.quickChosen = false;
        }

        this.state.selectedOptions = { ...closest.selectedOptions };
      }
    }

    this.render({ feedback: true });
  }

  render(options = {}) {
    const variant = this.getCurrentVariant();

    this.renderImage(variant, options);
    this.renderPrice(variant);
    this.renderLink(variant);
    this.renderTitle();
    this.renderDescription();
    this.renderOptions();
    this.renderQuickPanel();
    this.renderAddToCart(variant);
    this.renderWishlist();
  }

  // No panel when there's nothing to choose. After an add with no size picked,
  // its label asks for one.
  renderQuickPanel() {
    this.elements.containerSizes?.classList.toggle(
      "hidden",
      !this.needsChoice(),
    );

    const label = this.elements.quickLabel;

    if (!label) return;

    label.dataset.label ??= label.textContent;

    label.textContent = this.state.awaitingChoice
      ? label.dataset.hint
      : label.dataset.label;
  }

  renderImage(variant, options = {}) {
    if (!variant) return;

    const media = variant.media.items;

    if (!media.length) return;

    const image =
      this.state.isHovering && media.length > 1 ? media[1] : media[0];

    this.setImage(image, options.feedback === true);
  }

  setImage(image, feedback) {
    const target = this.elements.image;

    const alt = image.alt ?? this.product.title;

    // The live src is resolved against the origin, so it never matches the
    // protocol-relative one coming from the product JSON.
    if (target.dataset.currentSrc === image.src) {
      target.alt = alt;
      return;
    }

    target.dataset.currentSrc = image.src;

    const requestId = (this.imageRequestId ?? 0) + 1;

    this.imageRequestId = requestId;

    this.clearImageFeedback();

    // Nothing is painted yet on the first render, so there is no stale image to
    // protect and no reason to wait.
    if (!target.src) {
      target.src = image.src;
      target.alt = alt;
      return;
    }

    if (feedback) {
      this.imageFeedbackTimer = setTimeout(() => {
        if (this.imageRequestId !== requestId) return;

        target.style.transition = "opacity .2s ease";
        target.style.opacity = "0.55";
      }, IMAGE_SWAP_FEEDBACK_DELAY);
    }

    fetchImage(image.src).then(() => {
      // A newer selection already took over this card.
      if (this.imageRequestId !== requestId) return;

      this.clearImageFeedback();

      target.src = image.src;
      target.alt = alt;
    });
  }

  clearImageFeedback() {
    clearTimeout(this.imageFeedbackTimer);

    this.elements.image.style.opacity = "";
  }
  renderPrice(variant) {
    if (!variant) return;

    this.elements.price.textContent = VoraxCart.formatMoney(variant.price);
  }
  renderLink(variant) {
    if (!variant) return;

    this.elements.link.href = variant.url;
  }
  renderTitle() {
    this.elements.title.textContent = this.product.title;
  }
  renderDescription() {
    const colorOption = this.getColorOption();

    this.elements.description.textContent = colorOption
      ? this.state.selectedOptions[colorOption.name]
      : "";
  }

  renderOptions() {
    const { selectedOptions } = this.state;

    this.elements.optionButtons.forEach((button) => {
      const option = button.dataset.option;
      const value = button.dataset.value;

      const active = selectedOptions[option] === value;
      const available = this.isOptionAvailable(option, value);

      button.classList.toggle("active", active);

      button.disabled = !available;

      button.classList.toggle("disabled", !available);
    });
  }

  renderWishlist() {
    const button = this.element.querySelector(".favorite-btn");

    if (!button) return;

    const variant = this.getCurrentVariant();

    // A colour/size pair with no matching variant has no wishlist identity, so
    // leave the icon on its last known state instead of aborting the render.
    if (!variant) return;

    const icon = button.querySelector(".favorite-icon");

    const isFavorite = window.App.wishlist.exists(variant.id);

    button.classList.toggle("is-favorite", isFavorite);

    icon.src = isFavorite ? icon.dataset.filled : icon.dataset.empty;
  }

  renderAddToCart(variant) {
    const button = this.elements.addToCart;

    // With a size to pick, the button asks for one, so it only needs any
    // size of this colour in stock.
    if (this.needsChoice()) {
      const option = this.getQuickOption();

      button.disabled = !this.getQuickValues().some((value) =>
        this.isOptionAvailable(option.name, value.name),
      );

      return;
    }

    button.disabled = !variant?.available;
  }
}

// One broken card (bad JSON, missing markup) must not stop the rest of the
// grid from working. Cards already set up are skipped, so this is safe to
// call again after the filters swap the grid.
function initProductCards(root = document) {
  root.querySelectorAll(".collection-product-card").forEach((card) => {
    if (card.productCard) return;

    try {
      new ProductCard(card);
    } catch (error) {
      console.error(error);
    }
  });
}

class MobileSizeSheet {
  constructor() {
    this.container = document.querySelector("#mobileSizeSheet");
    this.overlay = document.querySelector("#mobileSizeSheetOverlay");
    this.content = document.querySelector("#mobileSizeSheetContent");
    this.options = document.querySelector("#mobileSizeOptions");
    this.titleSheet = document.querySelector(".title-sheet");

    this.productCard = null;

    this.bindEvents();
  }

  bindEvents() {
    this.overlay.addEventListener("click", () => this.close());

    this.options.addEventListener("click", (event) => {
      const option = event.target.closest(".mobile-size-option");

      if (!option || option.classList.contains("disabled")) return;

      this.onSizeSelected(option.dataset.value);
    });
  }

  async onSizeSelected(value) {
    const { productCard, option } = this;

    // A second tap lands after close() has already dropped the card.
    if (!productCard || !option) return;

    productCard.state.quickChosen = true;

    productCard.updateSelectedOption(option.name, value);

    // Read the variant before close(), which drops the card reference.
    const variant = productCard.getCurrentVariant();

    this.close();

    await productCard.addToCart(variant);
  }

  open(productCard) {
    const option = productCard.getQuickOption();

    // Nothing to choose. Cards add those straight away, so this is a guard.
    if (!option) return;

    // A close still animating would otherwise hide this sheet 300 ms later.
    clearTimeout(this.closeTimer);

    this.productCard = productCard;
    this.option = option;

    // Everything is filled in before the sheet is un-hidden, so a failure here
    // can never leave its invisible overlay covering the page.
    this.titleSheet.textContent = (
      window.variantStrings?.selectOption || "Select [option]"
    ).replace("[option]", option.name);

    this.render();

    this.container.classList.remove("hidden");

    requestAnimationFrame(() => {
      this.overlay.classList.remove("opacity-0");
      this.overlay.classList.add("opacity-100");

      this.content.classList.remove("translate-y-full");
      this.content.classList.add("translate-y-0");
    });
  }

  close(callback = null) {
    // Dropped now, not after the animation, so taps on a closing sheet do
    // nothing.
    this.productCard = null;

    this.overlay.classList.remove("opacity-100");
    this.overlay.classList.add("opacity-0");

    this.content.classList.remove("translate-y-0");
    this.content.classList.add("translate-y-full");

    clearTimeout(this.closeTimer);

    this.closeTimer = setTimeout(() => {
      this.container.classList.add("hidden");
      this.options.innerHTML = "";

      if (callback) {
        callback();
      }
    }, 300);
  }

  render() {
    this.renderSizes();
  }

  renderSizes() {
    this.options.innerHTML = "";

    const { productCard, option } = this;

    // Only the sizes that exist in the card's colour, with the card's own
    // availability.
    productCard.getQuickValues().forEach((size) => {
      const item = document.createElement("div");

      item.className =
        "mobile-size-option py-5 border-b border-gray-200 font-inter-medium cursor-pointer";

      item.dataset.value = size.name;

      item.textContent = size.name;

      if (!productCard.isOptionAvailable(option.name, size.name)) {
        item.classList.add("disabled");
      }

      this.options.appendChild(item);
    });
  }
}

// Created before the cards, which open it on mobile.
const mobileSizeSheet = new MobileSizeSheet();

initProductCards();

class ProductModal {
  constructor(element) {
    this.element = element;

    this.state = {
      product: null,
      quantity: 1,
      loading: false,
      error: null,
    };

    this.elements = this.cacheElements();

    this.bindEvents();
  }
  cacheElements() {
    return {
      overlay: this.element.querySelector("#cartModalOverlay"),

      close: this.element.querySelector("#closeCartModal"),

      image: this.element.querySelector("#modalProductImage"),

      title: this.element.querySelector("#modalProductTitle"),

      options: this.element.querySelector("#modalVariantOptions"),

      price: this.element.querySelector("#modalPrice"),

      qty: this.element.querySelector("#modalQty"),

      total: this.element.querySelector("#modalTotal"),

      minus: this.element.querySelector("#minusQty"),

      plus: this.element.querySelector("#plusQty"),

      continueShopping: this.element.querySelector("#continueShopping"),

      finalizePurchase: this.element.querySelector("#finalizePurchase"),

      error: this.element.querySelector("#modalError"),
    };
  }
  bindEvents() {
    this.elements.overlay.addEventListener("click", () => this.close());

    this.elements.close.addEventListener("click", () => this.close());

    this.elements.continueShopping.addEventListener("click", () =>
      this.close(),
    );

    this.elements.plus.addEventListener("click", () => {
      this.increaseQuantity();
    });

    this.elements.minus.addEventListener("click", () => {
      this.decreaseQuantity();
    });

    this.elements.finalizePurchase.addEventListener("click", () => {
      this.addToCart();
    });
  }
  open(product) {
    this.state.product = product;

    this.state.quantity = 1;

    this.state.loading = false;

    this.state.error = null;

    this.render();

    this.elements.overlay.classList.add("block");

    this.element.classList.remove("hidden");
  }
  close() {
    this.element.classList.add("hidden");
    this.state = {
      product: null,
      quantity: 1,
    };
  }
  increaseQuantity() {
    this.state.quantity++;

    this.renderQuantity();
    this.renderTotal();
  }

  decreaseQuantity() {
    if (this.state.quantity <= 1) return;

    this.state.quantity--;

    this.renderQuantity();
    this.renderTotal();
  }
  setLoading(value) {
    this.state.loading = value;

    this.elements.finalizePurchase.disabled = value;

    this.elements.finalizePurchase.textContent = value
      ? "Adding..."
      : "Finalize purchase";
  }
  setError(message) {
    this.state.error = message;

    this.elements.error.textContent = message;

    this.elements.error.classList.remove("hidden");
  }

  async addToCart() {
    const { variantId } = this.state.product;

    const quantity = this.state.quantity;

    try {
      this.setLoading(true);

      const response = await fetch(VoraxCart.url("cart/add.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        throw new Error(
          errorData.description || "Unable to add product to cart",
        );
      }

      const data = await response.json();

      window.location.href = VoraxCart.url("cart");
    } catch (error) {
      console.error(error);

      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  render() {
    if (!this.state.product) return;

    this.renderImage();

    this.renderTitle();

    this.renderOptions();

    this.renderPrice();

    this.renderQuantity();

    this.renderTotal();
  }
  renderImage() {
    const image = this.state.product.image;

    if (!image) return;

    this.elements.image.src = image.src;
    this.elements.image.alt = image.alt ?? this.state.product.title;
  }

  renderTitle() {
    this.elements.title.textContent = this.state.product.title;
  }

  renderOptions() {
    const { selectedOptions } = this.state.product;

    this.elements.options.innerHTML = "";

    Object.entries(selectedOptions).forEach(([name, value]) => {
      const option = document.createElement("p");

      option.className = "font-inter-bold text-secondary-gray text-2xl";

      option.innerHTML = `
      <span>${name}:</span>
      <span class="font-inter-light capitalize">${value}</span>
    `;

      this.elements.options.appendChild(option);
    });
  }

  renderPrice() {
    this.elements.price.textContent = VoraxCart.formatMoney(
      this.state.product.price,
    );
  }

  renderQuantity() {
    this.elements.qty.textContent = this.state.quantity;
  }

  renderTotal() {
    const total = this.state.product.price * this.state.quantity;

    this.elements.total.textContent = VoraxCart.formatMoney(total, true);
  }
}

// const productModal = new ProductModal(document.querySelector("#cartModal"));

// collections-filters.js calls this again after it swaps the grid.
window.initProductCards = initProductCards;
})();
